import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getBusinessByQrId } from "../api/business";
import MenuContent from "../components/MenuContent";
import CustomPageContent from "../components/CustomPageContent";
import AgendaContent from "../components/AgendaContent";
import BusinessBrandingHeader from "../components/BusinessBrandingHeader";
import QuickActionsPanel from "../components/QuickActionsPanel";
import PoweredByTipify from "../components/PoweredByTipify";
import BusinessViewSwitcher from "../components/BusinessViewSwitcher";
import { markMenuVisited } from "../utils/contenidoIntermedio";

const BusinessLandingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [businessData, setBusinessData] = useState(null);
  const [error, setError] = useState(null);
  const [inhabilitado, setInhabilitado] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        if (!id || id === "undefined" || id === "null") {
          setError("QR code no válido");
          setLoading(false);
          return;
        }

        const businessRes = await getBusinessByQrId(id);
        if (businessRes.status === 410) {
          setInhabilitado(true);
        } else if (businessRes.status === 200) {
          const responseData = businessRes.data;
          if (responseData.tipo === "business") {
            setBusinessData(responseData.business);
          } else {
            setError("No se encontró el business");
          }
        } else {
          setError("No se pudo cargar el business");
        }
      } catch (err) {
        console.error("Error al cargar business:", err);
        setError("Error al cargar el business");
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [id]);

  // Si no hay contenido intermedio, redirigir a FormScreen
  useEffect(() => {
    if (!loading && businessData && !businessData?.contenidoIntermedio?.activo) {
      navigate(`/${id}`, { replace: true });
    }
  }, [loading, businessData, navigate, id]);

  // Marcar que ya se ha visto la carta: la próxima lectura del QR
  // (dentro del TTL) aterrizará directamente en propinas
  useEffect(() => {
    if (!loading && businessData?.contenidoIntermedio?.activo) {
      markMenuVisited(businessData._id);
    }
  }, [loading, businessData]);

  if (loading) {
    return (
      <Container style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (inhabilitado) {
    return (
      <Container style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <Typography variant="h6">{t("businessDisabled.title")}</Typography>
        <Typography variant="body2" style={{ marginTop: "10px", maxWidth: 480 }}>
          {t("businessDisabled.description")}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <Typography variant="h6" color="error">{error}</Typography>
        <Typography variant="body2" style={{ marginTop: "10px" }}>
          Por favor, verifica el código QR e intenta de nuevo.
        </Typography>
      </Container>
    );
  }

  // Si hay contenido intermedio activo, mostrar el contenido correspondiente
  if (businessData?.contenidoIntermedio?.activo) {
    const tipo = businessData.contenidoIntermedio.tipo;
    const goToRatings = () => {
      // ?ratings=true evita que FormScreen redirija de vuelta a la carta
      window.location.href = `/${id}?ratings=true`;
    };
    // Mismo paddingTop que FormScreen para que el switcher quede a la misma altura
    const containerStyle = {
      minHeight: "100vh",
      padding: "4px 20px 20px",
    };
    const header = (
      <>
        <BusinessViewSwitcher
          tipo={tipo}
          active="content"
          onSelectTips={goToRatings}
        />
        <BusinessBrandingHeader
          business={businessData}
          showRating
          showLanguageSelector
        />
      </>
    );

    if (tipo === "menu") {
      return (
        <Container maxWidth="md" style={containerStyle}>
          {header}
          <MenuContent 
            menuConfig={businessData.contenidoIntermedio.configuracion}
            eventData={{ nombre: businessData.nombre_comercial, descripcion: businessData.descripcion }}
            contenidoIntermedio={businessData.contenidoIntermedio}
            onNavigateToRatings={goToRatings}
          />
          <QuickActionsPanel eventData={null} businessData={businessData} />
          <PoweredByTipify />
        </Container>
      );
    } else if (tipo === "pagina_personalizada") {
      return (
        <Container maxWidth="md" style={containerStyle}>
          {header}
          <CustomPageContent
            contenidoIntermedio={businessData.contenidoIntermedio}
            eventData={{ nombre: businessData.nombre_comercial, descripcion: businessData.descripcion }}
            businessData={businessData}
            onNavigateToRatings={goToRatings}
          />
          <QuickActionsPanel eventData={null} businessData={businessData} />
          <PoweredByTipify />
        </Container>
      );
    } else if (tipo === "agenda") {
      return (
        <Container maxWidth="md" style={containerStyle}>
          {header}
          <AgendaContent
            agendaConfig={businessData.contenidoIntermedio.configuracion}
            eventData={{ nombre: businessData.nombre_comercial, descripcion: businessData.descripcion }}
            contenidoIntermedio={businessData.contenidoIntermedio}
            onNavigateToRatings={goToRatings}
          />
          <QuickActionsPanel eventData={null} businessData={businessData} />
          <PoweredByTipify />
        </Container>
      );
    }
  }

  return null; // Mientras redirige
};

export default BusinessLandingPage;
